import React, { useState } from 'react';
import { sortByCountryThenName, paginate, PaginationControls } from './AdminTableUtils';

const getStatusBadge = (status) => {
  const styles = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
    approved: 'bg-green-500/20 text-green-400 border-green-500/50',
    rejected: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  };
  const label = status === 'rejected' ? 'WAITLISTED' : status.toUpperCase();
  return <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || ''}`}>{label}</span>;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getIntroducerDisplay = (request) => {
  if (request.introducer_type === 'public') return <span className="text-gray-500 text-xs">(Public)</span>;
  return (
    <div className="text-sm text-gray-300">
      {request.introduced_by}
      <span className="text-xs text-gray-500 ml-1">({request.introducer_type === 'user' ? 'User' : 'Trusted Professional'})</span>
    </div>
  );
};

const getFilterLabel = (filter) => {
  if (filter === 'rejected') return 'waitlisted';
  if (filter !== 'all') return filter;
  return '';
};

const getScoreStyle = (score) => {
  if (score >= 100) return 'bg-green-500 text-white';
  if (score >= 50) return 'bg-yellow-500 text-white';
  return 'bg-red-500 text-white';
};

function getRequestsTableContent({ loading, accessRequests, filter, requestsPage, setRequestsPage, actionLoading, onApprove, onWaitlist, onSelectRequest }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-500"></div>
      </div>
    );
  }
  if (accessRequests.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400 text-lg">No {getFilterLabel(filter)} requests found</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed">
        <thead className="bg-gray-700">
          <tr>
            <th className="w-[140px] px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
            <th className="w-[200px] px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
            <th className="w-[80px] px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Verified</th>
            <th className="w-[70px] px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Score</th>
            <th className="w-[90px] px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Experience</th>
            <th className="w-[130px] px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Introduced By</th>
            <th className="w-[90px] px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
            <th className="w-[260px] px-4 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider sticky right-0 bg-gray-700 z-10">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {paginate(sortByCountryThenName(accessRequests, 'country', (r) => `${r.first_name || ''} ${r.last_name || ''}`), requestsPage).map((request) => (
            <tr key={request.request_id} className="hover:bg-gray-700/50 transition group">
              <td className="px-4 py-4 text-sm font-medium text-white truncate" title={`${request.first_name} ${request.last_name}`}>{request.first_name} {request.last_name}</td>
              <td className="px-4 py-4 text-sm text-gray-300 truncate" title={request.email}>{request.email}</td>
              <td className="px-4 py-4">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${request.email_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {request.email_verified ? 'Yes' : 'No'}
                </span>
              </td>
              <td className="px-4 py-4">
                {request.approval_score != null ? (
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${getScoreStyle(request.approval_score)}`}>{request.approval_score}</span>
                ) : <span className="text-gray-500 text-xs">-</span>}
              </td>
              <td className="px-4 py-4 text-sm text-gray-300">{request.investment_experience || '-'}</td>
              <td className="px-4 py-4">{getIntroducerDisplay(request)}</td>
              <td className="px-4 py-4">{getStatusBadge(request.status)}</td>
              <td className="px-4 py-4 text-sm sticky right-0 bg-gray-800 group-hover:bg-gray-700/50 z-10 transition">
                <RequestRowActions request={request} actionLoading={actionLoading} onApprove={onApprove} onWaitlist={onWaitlist} onSelectRequest={onSelectRequest} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <PaginationControls currentPage={requestsPage} totalItems={accessRequests.length} onPageChange={setRequestsPage} />
    </div>
  );
}

const RequestRowActions = ({ request, actionLoading, onApprove, onWaitlist, onSelectRequest }) => (
  <div className="flex justify-end space-x-2">
    <button data-testid={`details-btn-${request.request_id}`} onClick={() => onSelectRequest(request)} className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-medium transition">Details</button>
    {request.status === 'pending' && (
      <>
        <button data-testid={`approve-btn-${request.request_id}`} onClick={() => onApprove(request.request_id)} disabled={actionLoading === request.request_id}
          className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-medium transition disabled:opacity-50">
          {actionLoading === request.request_id ? '...' : 'Approve'}
        </button>
        <button data-testid={`waitlist-btn-${request.request_id}`} onClick={() => onWaitlist(request.request_id)} disabled={actionLoading === request.request_id}
          className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-medium transition disabled:opacity-50">
          Waitlist
        </button>
      </>
    )}
    {request.status === 'rejected' && (
      <button data-testid={`approve-waitlisted-btn-${request.request_id}`} onClick={() => onApprove(request.request_id)} disabled={actionLoading === request.request_id}
        className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-medium transition disabled:opacity-50">
        {actionLoading === request.request_id ? '...' : 'Approve'}
      </button>
    )}
  </div>
);

export const AdminAccessRequestsTab = ({ accessRequests, loading, filter, setFilter, actionLoading, onApprove, onWaitlist, onSelectRequest }) => {
  const [requestsPage, setRequestsPage] = useState(1);

  const filterTabs = [
    { key: 'pending', label: 'Pending' }, { key: 'approved', label: 'Approved' },
    { key: 'waitlisted', label: 'Waitlisted' }, { key: 'all', label: 'All' },
    { key: 'public', label: 'Public' }, { key: 'user_intro', label: 'User Introductions' },
    { key: 'advisor_intro', label: 'Trusted Professional Introductions' }
  ];

  return (
    <>
      {/* Filter Tabs */}
      <div className="bg-gray-800 rounded-xl p-4 mb-6 border border-gray-700">
        <div className="flex flex-wrap gap-2">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setFilter(tab.key === 'waitlisted' ? 'rejected' : tab.key); setRequestsPage(1); }}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
                (filter === tab.key || (tab.key === 'waitlisted' && filter === 'rejected'))
                  ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {getRequestsTableContent({ loading, accessRequests, filter, requestsPage, setRequestsPage, actionLoading, onApprove, onWaitlist, onSelectRequest })}
      </div>
    </>
  );
};
